import type { MeetingPoint, RideDetail } from '@ridemaadi/types';
import {
  formatSnapshotDistance,
  formatSnapshotDuration,
  formatSnapshotStops,
} from '../../../components/rides/snapshotUtils';

export interface CompletionStats {
  distanceKm: number | null;
  durationMinutes: number | null;
  riderCount: number;
  stopCount: number;
}

export interface CompletionAchievement {
  emoji: string;
  title: string;
  subtitle: string;
}

const SEEN_KEY = 'rm-ride-celebration-seen';

function pointLabel(point: MeetingPoint): string {
  const name = point.name?.trim();
  if (name) return name;
  const address = point.address?.split(',')[0]?.trim();
  return address || 'Stop';
}

/** Route line for the celebration card (max 3 labels or origin → N Stops → destination). */
export function formatCompletionRoute(ride: RideDetail): string {
  const points = [...ride.meetingPoints].sort((a, b) => a.sortOrder - b.sortOrder);
  if (points.length >= 2) {
    if (points.length <= 3) {
      return points.map(pointLabel).join(' → ');
    }
    const stopLabel = points.length === 1 ? '1 Stop' : `${points.length} Stops`;
    return `${pointLabel(points[0]!)} → ${stopLabel} → ${pointLabel(points[points.length - 1]!)}`;
  }

  const origin =
    ride.route?.originAddress?.split(',')[0]?.trim() ??
    (points[0] ? pointLabel(points[0]) : null);
  const destination =
    ride.route?.destinationAddress?.split(',')[0]?.trim() ??
    (points[0] ? pointLabel(points[0]) : null);

  if (origin && destination && origin !== destination) {
    return `${origin} → ${destination}`;
  }
  if (origin) return origin;
  return ride.title;
}

export function getCompletionStats(ride: RideDetail): CompletionStats {
  const durationMinutes =
    ride.startedAt && ride.endedAt
      ? Math.max(0, Math.round((Date.parse(ride.endedAt) - Date.parse(ride.startedAt)) / 60_000))
      : ride.estimatedDurationMinutes;

  return {
    distanceKm: ride.route?.distanceKm ?? ride.estimatedDistanceKm,
    durationMinutes: durationMinutes && durationMinutes > 0 ? durationMinutes : ride.estimatedDurationMinutes,
    riderCount: ride.rsvpCounts.joined,
    stopCount: ride.meetingPoints.length,
  };
}

function isWeekendRide(rideDate: string): boolean {
  const day = new Date(`${rideDate}T12:00:00`).getDay();
  return day === 0 || day === 6;
}

/** Pick one compact achievement from ride statistics. */
export function generateCompletionAchievement(ride: RideDetail, stats: CompletionStats): CompletionAchievement {
  const distance = stats.distanceKm ?? 0;
  const duration = stats.durationMinutes ?? 0;
  const riders = stats.riderCount;

  if (distance >= 200 || duration >= 480) {
    return {
      emoji: '⭐',
      title: 'Epic Weekend Ride',
      subtitle: 'A long haul worth remembering.',
    };
  }

  if (isWeekendRide(ride.rideDate) && distance >= 80) {
    return {
      emoji: '⭐',
      title: 'Epic Weekend Ride',
      subtitle: 'Weekend miles with your crew.',
    };
  }

  if (riders >= 8) {
    return {
      emoji: '🎉',
      title: 'Perfect Group',
      subtitle: 'Everyone completed the ride.',
    };
  }

  if (duration >= 360 && distance >= 120) {
    return {
      emoji: '🏅',
      title: 'Smooth Ride',
      subtitle: 'Long day, steady pace, no fuss.',
    };
  }

  return {
    emoji: '🏅',
    title: 'Smooth Ride',
    subtitle: 'No incidents reported.',
  };
}

type MemoryBuilder = (ctx: {
  distance: string;
  riders: number;
  route: string;
  title: string;
}) => string;

const RIDE_MEMORIES: MemoryBuilder[] = [
  ({ distance, riders }) => `${distance}. ${riders} riders. One unforgettable day.`,
  () => 'Great roads. Great friends. Great memories.',
  ({ route }) => `${route}-another adventure completed safely.`,
  ({ title }) => `${title} is in the books. Ride on.`,
  ({ distance, route }) => `${distance} across ${route}. Well ridden.`,
  ({ riders }) => `${riders} riders, one shared journey-until the next one.`,
];

function hashRideId(rideId: string): number {
  let hash = 0;
  for (let i = 0; i < rideId.length; i += 1) {
    hash = (hash * 31 + rideId.charCodeAt(i)) >>> 0;
  }
  return hash;
}

/** Personalized ride memory - stable per ride, rotates across rides. */
export function generateRideMemory(ride: RideDetail, stats: CompletionStats): string {
  const route = formatCompletionRoute(ride);
  const distance = formatSnapshotDistance(stats.distanceKm);
  const idx = hashRideId(ride.id) % RIDE_MEMORIES.length;
  return RIDE_MEMORIES[idx]!({
    distance,
    riders: stats.riderCount,
    route,
    title: ride.title,
  });
}

export function formatCompletionStatCells(stats: CompletionStats): [string, string, string, string] {
  return [
    formatSnapshotDistance(stats.distanceKm),
    formatSnapshotDuration(stats.durationMinutes),
    stats.riderCount === 1 ? '1 Rider' : `${stats.riderCount} Riders`,
    formatSnapshotStops(stats.stopCount),
  ];
}

export function firstNameFromDisplayName(displayName: string | null | undefined): string {
  const trimmed = displayName?.trim();
  if (!trimmed) return 'Rider';
  return trimmed.split(/\s+/)[0] ?? 'Rider';
}

export function wasCelebrationShown(rideId: string): boolean {
  try {
    const raw = sessionStorage.getItem(SEEN_KEY);
    if (!raw) return false;
    const ids = JSON.parse(raw) as string[];
    return ids.includes(rideId);
  } catch {
    return false;
  }
}

export function markCelebrationShown(rideId: string): void {
  try {
    const raw = sessionStorage.getItem(SEEN_KEY);
    const ids = raw ? (JSON.parse(raw) as string[]) : [];
    if (!ids.includes(rideId)) {
      ids.push(rideId);
      sessionStorage.setItem(SEEN_KEY, JSON.stringify(ids.slice(-30)));
    }
  } catch {
    /* ignore storage errors */
  }
}
